import { useState, useEffect, useMemo } from 'react';
import { apiGet } from './apiClient';

// ---------------------------------------------------------------------------
// Metric registry — mirrors _METRIC_REGISTRY in api_screener_conviction.py.
// `value` is the sort_by sent to the server; `label` is what the user sees;
// `format` tells the UI how to render the echoed-back sort_value.
// ---------------------------------------------------------------------------

export type ConvictionMetric =
    | 'dominant_capital'
    | 'capital_yes'
    | 'capital_no'
    | 'raw_implied'
    | 'trader_count'
    | 'edge_disparity_abs'
    | 'avg_edge_yes'
    | 'avg_edge_no'
    | 'imbalance';

export interface MetricDef {
    value: ConvictionMetric;
    label: string;
    group: 'Capital' | 'Traders' | 'Edge';
    format: 'usd' | 'count' | 'ratio' | 'prob';
}

export const CONVICTION_METRICS: MetricDef[] = [
    { value: 'dominant_capital', label: 'Dominant capital', group: 'Capital', format: 'usd' },
    { value: 'capital_yes', label: 'Capital · Yes', group: 'Capital', format: 'usd' },
    { value: 'capital_no', label: 'Capital · No', group: 'Capital', format: 'usd' },
    { value: 'trader_count', label: 'Trader count', group: 'Traders', format: 'count' },
    { value: 'raw_implied', label: 'Raw implied', group: 'Edge', format: 'prob' },
    { value: 'imbalance', label: 'Edge imbalance', group: 'Edge', format: 'ratio' },
    { value: 'edge_disparity_abs', label: 'Edge disparity', group: 'Edge', format: 'ratio' },
    { value: 'avg_edge_yes', label: 'Avg edge · Yes', group: 'Edge', format: 'ratio' },
    { value: 'avg_edge_no', label: 'Avg edge · No', group: 'Edge', format: 'ratio' },
];

export const DEFAULT_METRIC: ConvictionMetric = 'dominant_capital';

// Valid filter vocabularies — mirror the backend constants so we never send
// a value the endpoint will 400 on.
export const VALID_POLYMARKET_TAGS = [
    'politics', 'sports', 'crypto', 'esports', 'iran', 'finance',
    'geopolitics', 'tech', 'pop-culture', 'economy', 'weather',
    'mentions', 'elections',
] as const;

export const VALID_EVENT_CATEGORIES = [
    'geopolitical', 'macro_release', 'price_level', 'corporate', 'policy_decision',
] as const;

export type PolymarketTag = (typeof VALID_POLYMARKET_TAGS)[number];
export type EventCategory = (typeof VALID_EVENT_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Response types — mirror the Pydantic models in api_screener_conviction.py
// ---------------------------------------------------------------------------

export type DominantSide = 'YES' | 'NO';

// The per-score `group_aggregation` block inside `breakdown`. Typed loosely
// (index signature) because the backend may add fields over time, but the
// values the UI reads are named explicitly so they stay type-checked.
export interface GroupAggregation {
    eq_avg_entry_price_yes?: number | null;
    eq_avg_entry_price_no?: number | null;
    cw_avg_edge_yes?: number | null;
    cw_avg_edge_no?: number | null;
    imbalance?: number | null;
    [key: string]: unknown;
}

// The full conviction breakdown returned by the backend. It is "just JSON" —
// we don't model every nested field, only what the UI consumes.
export interface ConvictionBreakdown {
    group_aggregation?: GroupAggregation;
    [key: string]: unknown;
}

export interface ConvictionMarket {
    market_id: string;
    market_question: string | null;
    conviction_outcome: string | null;
    outcome_yes_label: string | null;
    outcome_no_label: string | null;
    current_yes_prob: number | null;
    volume: number | null;
    start_date: string | null;
    event_id: string | null;
    event_title: string | null;
    event_slug: string | null;
    event_category: string | null;
    polymarket_tags: string[];
    dominant_side: DominantSide | null;
    sort_value: number | null;
    capital_usd_yes: number;
    capital_usd_no: number;
    n_traders_yes: number;
    n_traders_no: number;
    raw_implied: number | null;
    conviction_ts: string | null;
    signal_count: number;
    open_signal_count: number;
    // Full conviction breakdown JSON (may be absent on older cached responses).
    breakdown: ConvictionBreakdown | null;
}

export interface ConvictionScreenerResponse {
    group: string;
    sort_by: string;
    filters: Record<string, unknown>;
    markets: ConvictionMarket[];
}

export interface EventSiblingMarket {
    market_id: string;
    market_question: string | null;
    outcome_yes_label: string | null;
    outcome_no_label: string | null;
    current_yes_prob: number | null;
    volume: number | null;
    closed: boolean;
    has_conviction: boolean;
    dominant_side: DominantSide | null;
    raw_implied: number | null;
    capital_usd_yes: number | null;
    capital_usd_no: number | null;
    n_traders_yes: number | null;
    n_traders_no: number | null;
}

export interface EventMarketsPanel {
    event_id: string;
    event_title: string | null;
    event_slug: string | null;
    group: string;
    markets: EventSiblingMarket[];
}

// ---------------------------------------------------------------------------
// Query params for the screener
// ---------------------------------------------------------------------------

export interface ConvictionScreenerParams {
    group: string;
    sortBy: ConvictionMetric;
    polymarketTags?: string[];
    eventCategories?: string[];
    minCapital?: number | null;
    minTraders?: number | null;
    minYesProb?: number | null;
    maxYesProb?: number | null;
    minEdgeDisparity?: number | null;
    minDaysRunning?: number | null;
    maxDaysRunning?: number | null;
    maxAgeDays?: number | null;
    hasSignal?: boolean;
    includeTraderScores?: boolean;
    limit?: number;
}

function buildQueryString(p: ConvictionScreenerParams): string {
    const qs = new URLSearchParams();
    qs.set('group', p.group);
    qs.set('sort_by', p.sortBy);

    if (p.includeTraderScores) {
        qs.set('include_trader_scores', 'true');
    }
    if (p.polymarketTags && p.polymarketTags.length > 0) {
        qs.set('polymarket_tag', p.polymarketTags.join(','));
    }
    if (p.eventCategories && p.eventCategories.length > 0) {
        qs.set('event_category', p.eventCategories.join(','));
    }
    if (p.minCapital != null) qs.set('min_capital', String(p.minCapital));
    if (p.minTraders != null) qs.set('min_traders', String(p.minTraders));
    if (p.minYesProb != null) qs.set('min_yes_prob', String(p.minYesProb));
    if (p.maxYesProb != null) qs.set('max_yes_prob', String(p.maxYesProb));
    if (p.minEdgeDisparity != null)
        qs.set('min_edge_disparity', String(p.minEdgeDisparity));
    if (p.minDaysRunning != null)
        qs.set('min_days_running', String(p.minDaysRunning));
    if (p.maxDaysRunning != null)
        qs.set('max_days_running', String(p.maxDaysRunning));
    if (p.maxAgeDays != null)
        qs.set('max_age_days', String(p.maxAgeDays));
    // has_signal is a tri-state on the backend; only send it when filtering on.
    if (p.hasSignal) qs.set('has_signal', 'true');
    qs.set('limit', String(p.limit ?? 50));

    return qs.toString();
}

// ---------------------------------------------------------------------------
// useConvictionScreener — re-fetches whenever the query changes, because the
// server owns ranking and the returned sort_value depends on sort_by.
// ---------------------------------------------------------------------------

export function useConvictionScreener(params: ConvictionScreenerParams): {
    data: ConvictionScreenerResponse | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<ConvictionScreenerResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Stable dependency key so we only re-fetch on a real query change.
    const query = useMemo(() => buildQueryString(params), [params]);

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<ConvictionScreenerResponse>(
            `/api/screener/conviction?${query}`,
            controller.signal
        )
            .then(res => {
                if (!cancelled) setData(res);
            })
            .catch(err => {
                if (cancelled || err?.name === 'AbortError') return;
                setError(err.message ?? 'Failed to load conviction screener');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [query]);

    return { data, loading, error };
}

// ---------------------------------------------------------------------------
// useEventMarkets — lazy expand panel. Pass null to skip fetching.
// ---------------------------------------------------------------------------

export function useEventMarkets(
    eventId: string | null,
    group: string
): {
    data: EventMarketsPanel | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<EventMarketsPanel | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!eventId) {
            setData(null);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setData(null);

        const qs = new URLSearchParams({ group });
        apiGet<EventMarketsPanel>(
            `/api/screener/conviction/event/${encodeURIComponent(eventId)}?${qs.toString()}`,
            controller.signal
        )
            .then(res => {
                if (!cancelled) setData(res);
            })
            .catch(err => {
                if (cancelled || err?.name === 'AbortError') return;
                setError(err.message ?? 'Failed to load event markets');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [eventId, group]);

    return { data, loading, error };
}